import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Globe2, FlaskConical, Microscope, ShieldCheck, Users, Beaker, ArrowRight, FileText } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1581093588401-fbb62a02f120?auto=format&fit=crop&w=1600&q=80";
const LAB_IMG_1 =
  "https://images.unsplash.com/photo-1532187863486-abf9dbad1b69?auto=format&fit=crop&w=1200&q=80";
const LAB_IMG_2 =
  "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?auto=format&fit=crop&w=1200&q=80";
const LAB_IMG_3 =
  "https://images.unsplash.com/photo-1579154204601-01588f351e67?auto=format&fit=crop&w=1200&q=80";
const NZ_IMG =
  "https://images.unsplash.com/photo-1507699622108-4be3abd695ad?auto=format&fit=crop&w=1600&q=80";

const STATS = [
  { value: "12+", label: "Years of Research" },
  { value: "40+", label: "Formulas Developed" },
  { value: "500+", label: "Physio Clinics Served" },
  { value: "9", label: "Countries Shipped To" },
];

const PILLARS = [
  {
    icon: Microscope,
    title: "Clinical R&D",
    desc: "Our lab in Auckland works with practising physiotherapists to translate rehabilitation evidence into everyday formulas — clinically dosed, therapist-tested.",
  },
  {
    icon: FlaskConical,
    title: "Nutraceutical Science",
    desc: "Every ingredient is sourced against pharmacopoeial specifications. Blends are stability-tested, third-party assayed and released only after batch documentation is complete.",
  },
  {
    icon: ShieldCheck,
    title: "Global Compliance",
    desc: "Facilities operate under GMP and WHO-GMP protocols, with manufacturing partners USFDA-registered — the same standards demanded by regulated medicine.",
  },
  {
    icon: Users,
    title: "Physio-Led Advisory",
    desc: "A rotating council of orthopedic and neurological physiotherapists reviews every launch, so what reaches your bottle is what the clinic would actually recommend.",
  },
];

export default function About() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen pt-[68px] lg:pt-[116px]" data-testid="about-page">
      {/* HERO */}
      <section className="section-dark relative overflow-hidden py-24 sm:py-32">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="GD Global Health research laboratory" className="h-full w-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0A2540]/70 via-[#0A2540]/85 to-[#0A2540]" />
        </div>
        <div className="relative z-10 mx-auto max-w-[1400px] px-5 sm:px-8">
          <div className="flex items-center gap-3 font-mono text-xs uppercase tracking-overline text-[#34D8A0]">
            <span className="h-px w-10 bg-[#34D8A0]" />
            <Globe2 className="h-3.5 w-3.5" /> Our Science Partner · Auckland, New Zealand
          </div>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="mt-6 max-w-3xl font-display text-5xl font-black leading-[1.02] tracking-tight sm:text-6xl lg:text-7xl"
            data-testid="about-hero-title"
          >
            Where every<br />
            <span className="text-[#34D8A0]">Physio Essential</span> formula begins.
          </motion.h1>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-white/70">
            Physio Essential products are researched and developed at <span className="font-semibold text-white">GD Global Health Ltd</span>, a clinical nutraceutical R&amp;D house headquartered in New Zealand. Every SKU we sell in India traces back to their labs — and to the physiotherapy science that guides them.
          </p>
        </div>
      </section>

      {/* STATS */}
      <section className="border-y border-black/10 bg-white px-5 py-14 sm:px-8">
        <div className="mx-auto grid max-w-[1400px] grid-cols-2 gap-8 md:grid-cols-4">
          {STATS.map((s) => (
            <div key={s.label} className="text-center md:text-left">
              <div className="font-display text-4xl font-black tracking-tight text-[#0A2540] sm:text-5xl">
                {s.value}
              </div>
              <div className="mt-2 font-mono text-[11px] uppercase tracking-overline text-black/50">
                {s.label}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* STORY + LAB IMAGES */}
      <section className="px-5 py-20 sm:px-8 sm:py-28">
        <div className="mx-auto max-w-[1400px]">
          <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
            <div>
              <p className="font-mono text-xs uppercase tracking-overline text-[#2F80ED]">The GD Global Health story</p>
              <h2 className="mt-4 font-display text-4xl font-black leading-tight tracking-tight sm:text-5xl">
                Built by physios,<br />
                <span className="italic text-[#2F80ED]">for physios.</span>
              </h2>
              <div className="mt-6 space-y-4 text-base leading-relaxed text-black/70">
                <p>
                  GD Global Health Limited was founded to answer a frustration heard in every rehab clinic in the world: the pills patients bought at pharmacies rarely matched the doses studied in real physical-therapy trials. Sports medicine researchers, formulators and practising physiotherapists came together in Auckland to change that.
                </p>
                <p>
                  A decade of R&amp;D later, GD Global Health now licenses its formula IP across countries — with Physio Essential as its exclusive Indian recovery-supplement brand. From cartilage biology to peripheral-nerve rehabilitation, every product ships only after clinical review, stability studies and independent assay.
                </p>
                <p>
                  Manufacturing partners are held to <strong className="text-black">GMP</strong>, <strong className="text-black">WHO-GMP</strong> and <strong className="text-black">USFDA</strong> facility standards — the same pharmaceutical infrastructure used for prescription products.
                </p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <motion.img
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7 }}
                src={LAB_IMG_1}
                alt="Clinical formulator preparing an assay"
                className="col-span-2 h-64 w-full object-cover"
              />
              <motion.img
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: 0.1 }}
                src={LAB_IMG_2}
                alt="Analytical laboratory bench"
                className="h-56 w-full object-cover"
              />
              <motion.img
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: 0.2 }}
                src={LAB_IMG_3}
                alt="Ingredient QC in progress"
                className="h-56 w-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* OFFICIAL REGISTRATION */}
      <section className="border-y border-black/10 bg-white px-5 py-16 sm:px-8 sm:py-20" data-testid="registration-section">
        <div className="mx-auto max-w-[1400px]">
          <div className="grid gap-12 lg:grid-cols-[1fr_1.4fr] lg:items-center">
            <div>
              <p className="font-mono text-xs uppercase tracking-overline text-[#2F80ED]">Officially Registered</p>
              <h2 className="mt-3 font-display text-3xl font-black leading-tight tracking-tight sm:text-4xl">
                Incorporated under the<br />
                <span className="text-[#2F80ED]">Companies Act 1993</span>, New Zealand.
              </h2>
              <p className="mt-4 max-w-md text-sm leading-relaxed text-black/60">
                GD Global Health Limited is a legally incorporated New Zealand entity. Below are our verifiable public registry identifiers — searchable on the New Zealand Companies Office register.
              </p>
            </div>
            <div className="relative overflow-hidden border-2 border-[#0A2540]/10 bg-[#F9F9F6] p-8">
              {/* Watermark seal */}
              <div className="pointer-events-none absolute -right-8 -top-8 h-40 w-40 opacity-[0.05]">
                <FileText className="h-full w-full text-[#0A2540]" strokeWidth={0.8} />
              </div>
              <div className="flex items-start gap-4">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border-2 border-[#0A2540]/20 bg-white">
                  <FileText className="h-5 w-5 text-[#0A2540]" />
                </div>
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-overline text-black/50">Certificate of Incorporation</div>
                  <div className="mt-1 font-display text-xl font-bold tracking-tight text-[#0A2540]">GD Global Health Limited</div>
                </div>
              </div>
              <dl className="mt-6 grid gap-x-8 gap-y-4 sm:grid-cols-2" data-testid="reg-details">
                <div>
                  <dt className="font-mono text-[10px] uppercase tracking-overline text-black/50">Company Number</dt>
                  <dd className="mt-1 font-mono text-base font-bold text-[#0A2540]">9296068</dd>
                </div>
                <div>
                  <dt className="font-mono text-[10px] uppercase tracking-overline text-black/50">NZBN</dt>
                  <dd className="mt-1 font-mono text-base font-bold text-[#0A2540]">9429052481291</dd>
                </div>
                <div>
                  <dt className="font-mono text-[10px] uppercase tracking-overline text-black/50">Incorporated</dt>
                  <dd className="mt-1 font-mono text-base font-bold text-[#0A2540]">25 November 2024</dd>
                </div>
                <div>
                  <dt className="font-mono text-[10px] uppercase tracking-overline text-black/50">Jurisdiction</dt>
                  <dd className="mt-1 font-mono text-base font-bold text-[#0A2540]">New Zealand</dd>
                </div>
                <div className="sm:col-span-2">
                  <dt className="font-mono text-[10px] uppercase tracking-overline text-black/50">Statute</dt>
                  <dd className="mt-1 font-mono text-sm font-semibold text-[#0A2540]">Incorporated under the Companies Act 1993 · Registrar of Companies, NZ</dd>
                </div>
              </dl>
            </div>
          </div>
        </div>
      </section>

      {/* PILLARS */}
      <section className="bg-[#F9F9F6] px-5 py-20 sm:px-8 sm:py-28">
        <div className="mx-auto max-w-[1400px]">
          <div className="flex flex-col justify-between gap-6 sm:flex-row sm:items-end">
            <div>
              <p className="font-mono text-xs uppercase tracking-overline text-[#2F80ED]">How we build a formula</p>
              <h2 className="mt-3 font-display text-4xl font-black leading-tight tracking-tight sm:text-5xl">
                Four pillars, zero shortcuts.
              </h2>
            </div>
          </div>
          <div className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {PILLARS.map((p, i) => {
              const Icon = p.icon;
              return (
                <motion.div
                  key={p.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.7, delay: (i % 4) * 0.08 }}
                  className="border border-black/10 bg-white p-7 transition-colors hover:border-[#2F80ED]"
                  data-testid={`pillar-${i}`}
                >
                  <div className="flex h-12 w-12 items-center justify-center rounded-full border border-[#2F80ED]/30 bg-[#2F80ED]/10">
                    <Icon className="h-5 w-5 text-[#2F80ED]" strokeWidth={1.6} />
                  </div>
                  <h3 className="mt-6 font-display text-lg font-bold tracking-tight text-[#0A2540]">{p.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-black/60">{p.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* NEW ZEALAND VISUAL BLOCK */}
      <section className="relative overflow-hidden bg-[#0A2540]">
        <img src={NZ_IMG} alt="New Zealand landscape" className="absolute inset-0 h-full w-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A2540] via-[#0A2540]/70 to-transparent" />
        <div className="relative z-10 mx-auto max-w-[1400px] px-5 py-24 text-white sm:px-8 sm:py-32">
          <p className="font-mono text-xs uppercase tracking-overline text-[#34D8A0]">Anchored in Auckland</p>
          <h2 className="mt-4 max-w-xl font-display text-4xl font-black leading-tight tracking-tight sm:text-5xl">
            A quieter kind of science.
          </h2>
          <p className="mt-6 max-w-lg text-base leading-relaxed text-white/70">
            New Zealand has some of the world&apos;s cleanest supply chains and strictest food-and-medicine oversight. GD Global Health chose Auckland deliberately — for the water, the botanicals, the pace of thinking, and the room to do research properly.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-white px-5 py-20 text-center sm:px-8">
        <div className="mx-auto max-w-3xl">
          <p className="font-mono text-xs uppercase tracking-overline text-[#2F80ED]">The result</p>
          <h2 className="mt-4 font-display text-4xl font-black leading-tight tracking-tight sm:text-5xl">
            Formulas your physiotherapist would actually pick.
          </h2>
          <Link
            to="/shop"
            className="mt-10 inline-flex items-center gap-3 rounded-full bg-[#0A2540] px-8 py-4 font-semibold text-white transition-colors hover:bg-[#2F80ED]"
            data-testid="about-shop-cta"
          >
            <Beaker className="h-4 w-4" /> Explore the formulas <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
